import React, { useState, useEffect } from 'react';
import { visitorsAPI } from '../../utils/api';
import StatCard from '../../components/StatCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function SiteVisitorsPage() {
    const [visitorSummary, setVisitorSummary] = useState(null);
    const [visitorTimeSeries, setVisitorTimeSeries] = useState([]);
    const [timeframe, setTimeframe] = useState('7d');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, [timeframe]);

    const loadData = async () => {
        setLoading(true);
        try {
            const days = timeframe === '24h' ? 1 : timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
            const [summaryRes, timeSeriesRes] = await Promise.all([
                visitorsAPI.getSummary(timeframe),
                visitorsAPI.getTimeSeries(days),
            ]);
            setVisitorSummary(summaryRes.data);
            setVisitorTimeSeries(timeSeriesRes.data);
        } catch (error) {
            console.error('Error loading visitor data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !visitorSummary) return <div className="text-gray-400">Loading...</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Site Visitors</h2>
                <select
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-zebcast-purple"
                >
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="all">All Time</option>
                </select>
            </div>

            {/* Visitor Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <StatCard title="Page Views" value={visitorSummary?.totalPageViews || 0} subtitle="Total views" icon="Eye" />
                <StatCard title="Unique Visitors" value={visitorSummary?.uniqueVisitors || 0} subtitle="By session" icon="Users" />
                <StatCard title="Unique IPs" value={visitorSummary?.uniqueIPs || 0} subtitle="IP addresses" icon="Globe" />
            </div>

            {/* Visitor Time Series Chart */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Visitors Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={visitorTimeSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="views" name="Page Views" stroke="#667eea" strokeWidth={2} />
                        <Line type="monotone" dataKey="visitors" name="Unique Visitors" stroke="#f093fb" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Top Pages */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Top Pages</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(visitorSummary?.topPages || []).map((page, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">{page.page}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{page.views}</td>
                                    </tr>
                                ))}
                                {(!visitorSummary?.topPages || visitorSummary.topPages.length === 0) && (
                                    <tr><td colSpan="2" className="px-4 py-6 text-sm text-gray-400 text-center">No data yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Top Referrers */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Top Referrers</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(visitorSummary?.topReferrers || []).map((ref, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900 truncate max-w-xs">{ref.referrer || '(direct)'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{ref.count}</td>
                                    </tr>
                                ))}
                                {(!visitorSummary?.topReferrers || visitorSummary.topReferrers.length === 0) && (
                                    <tr><td colSpan="2" className="px-4 py-6 text-sm text-gray-400 text-center">No data yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
